import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";

export const fetchJobPostings = async () => {
  try {
    const response = await axiosInstance.get(API.hiringJobPosting.getAllPostings);
    const postings = Array.isArray(response.data) ? response.data : [];
    const filtered = postings.filter((posting) => posting.status === 0 || posting.status === 3);
    return filtered;
  } catch (error) {
    console.error("Error fetching employee numbers:", error);
    return [];
  }
};

export const fetchEmployeeNumbers = async () => {
  try {
    const response = await axiosInstance.get(API.hiringJobPosting.getAllPostings);
    const postings = Array.isArray(response.data) ? response.data : [];
    const filtered = postings.filter((posting) => posting.status === 0 || posting.status === 3);
    return filtered;
  } catch (error) {
    console.error("Error fetching employee numbers:", error);
    return [];
  }
};

export const fetchFeedbackEmployees = async (jobPostingId) => {
  try {
    const response = await axiosInstance.get(API.hiringJobApplicant.getListByPosting(jobPostingId));
    const applicants = Array.isArray(response.data?.applicants) ? response.data?.applicants : [];
    return applicants;
  } catch (error) {
    console.error("Error fetching employee details:", error);
    return null;
  }
};

export const fetchEmployeeData = async (jobApplicantId) => {
  try {
    const response = await axiosInstance.get(API.hiringJobApplicant.getById(jobApplicantId));
    return response.data || null;
  } catch (error) {
    console.error("Error fetching employee details:", error);
    return null;
  }
};

export const submitEmployeeDocuments = async (formData) => {
  try {
    const response = await axiosInstance.post(API.hiringContracts.sendContracts, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      // Increase timeout to 5 minutes (300,000ms)
      // Or set to 0 to wait indefinitely
      timeout: 300000,
    });

    return response.data;
  } catch (error) {
    console.error("Full Error Details:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      headers: error.response?.headers,
    });

    // Specific check for timeout to give a better error message
    if (error.code === "ECONNABORTED") {
      throw "Upload timed out. The files are too large or your connection is slow.";
    }

    const errorMsg =
      error.response?.data?.message ||
      error.response?.data?.details ||
      error.message ||
      "Server error. Please contact support.";
    throw errorMsg;
  }
};

// ---- Hiring Document Template Repository (Mudassir Jul-15 handoff) ----
// These back the Company Documents page. The interceptor adds the auth token
// and strips Content-Type for FormData, so uploads only need the FormData body.

export const fetchDocumentTemplates = async () => {
  const response = await axiosInstance.get(API.hiringDocumentTemplate.getList);
  return Array.isArray(response.data) ? response.data : [];
};

// Same endpoint for first upload and replace. Do NOT send a version; the
// backend generates the next one.
export const uploadDocumentTemplate = async (documentType, file) => {
  const formData = new FormData();
  formData.append("documentType", documentType);
  formData.append("file", file);
  const response = await axiosInstance.put(API.hiringDocumentTemplate.upload, formData, {
    timeout: 300000,
  });
  return response.data;
};

export const fetchTemplateHistory = async (documentType) => {
  const response = await axiosInstance.get(API.hiringDocumentTemplate.history(documentType));
  return Array.isArray(response.data) ? response.data : [];
};

// Pull a filename out of the Content-Disposition header when the backend sends
// one, otherwise fall back to the passed-in name.
const resolveFileName = (contentDisposition, fallback) => {
  if (contentDisposition) {
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
    if (match?.[1]) return decodeURIComponent(match[1]);
  }
  return fallback || "document";
};

const triggerBlobDownload = (blobData, fileName) => {
  const url = window.URL.createObjectURL(new Blob([blobData]));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadCurrentTemplate = async (documentType, fallbackName) => {
  const response = await axiosInstance.get(
    API.hiringDocumentTemplate.downloadCurrent(documentType),
    { responseType: "blob" }
  );
  triggerBlobDownload(
    response.data,
    resolveFileName(response.headers?.["content-disposition"], fallbackName)
  );
};

export const downloadTemplateVersion = async (hiringDocumentTemplateId, fallbackName) => {
  const response = await axiosInstance.get(
    API.hiringDocumentTemplate.downloadVersion(hiringDocumentTemplateId),
    { responseType: "blob" }
  );
  triggerBlobDownload(
    response.data,
    resolveFileName(response.headers?.["content-disposition"], fallbackName)
  );
};
